'use client';
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { PASS_MARK, TOTAL_QUESTIONS } from '../data/questions';

interface QuizHeaderProps {
  currentQuestion: number;
  totalQuestions: number;
  score: number;
  progressPercent: number;
  category: string;
}

const MILESTONES = [10, 20, 30, 40, 50];

export default function QuizHeader({
  currentQuestion,
  totalQuestions,
  score,
  progressPercent,
  category,
}: QuizHeaderProps) {
  const remaining = totalQuestions - currentQuestion + 1;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid #E2E8F0',
        boxShadow: '0 1px 12px rgba(15,23,42,0.06)',
      }}
      aria-label="Quiz progress header"
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
        {/* Top row */}
        <div className="h-14 flex items-center justify-between gap-3">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0" aria-label="Galaxy Driving Academy — Return home">
            <div className="relative w-28 h-8 flex-shrink-0">
              <Image
                src="/assets/images/cropped-Untitled-1-scaled-1-1780379093642.webp"
                alt="Galaxy Driving Academy logo"
                fill
                className="object-contain object-left"
                priority
              />
            </div>
          </Link>

          {/* Question counter */}
          <div className="flex items-center gap-1.5 text-sm shrink-0">
            <span className="font-medium hidden sm:inline" style={{ color: '#94A3B8' }}>Q</span>
            <span className="font-mono-nums font-bold text-base" style={{ color: '#0F172A' }}>
              {currentQuestion}
            </span>
            <span style={{ color: '#CBD5E1' }}>/</span>
            <span className="font-mono-nums font-medium" style={{ color: '#64748B' }}>
              {totalQuestions}
            </span>
          </div>

          {/* Category tag — hidden on mobile */}
          <div className="hidden lg:flex">
            <span className="category-tag text-xs">{category}</span>
          </div>

          {/* Remaining */}
          <div className="hidden md:flex items-center gap-1.5 text-xs shrink-0" style={{ color: '#64748B' }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="5" stroke="#94A3B8" strokeWidth="1.2" />
              <path d="M6 3v3.5l2 1" stroke="#94A3B8" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <span className="font-mono-nums font-semibold" style={{ color: '#475569' }}>{remaining}</span>
            <span>left</span>
          </div>

          {/* Percentage */}
          <div className="hidden sm:flex items-center gap-1 text-xs shrink-0">
            <span className="font-mono-nums font-bold" style={{ color: '#1E40AF' }}>{progressPercent}%</span>
            <span style={{ color: '#94A3B8' }}>done</span>
          </div>

          {/* Pass mark badge */}
          <div
            className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0"
            style={{ background: 'rgba(30,64,175,0.07)', color: '#1E40AF', border: '1px solid rgba(30,64,175,0.15)' }}
            title="Official pass mark: 43 out of 50"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M5 1L2 3.5v4c0 1.5 1.5 2.5 3 2.5s3-1 3-2.5v-4L5 1z" fill="rgba(30,64,175,0.2)" stroke="#1E40AF" strokeWidth="0.8" />
            </svg>
            Pass: {PASS_MARK}/{TOTAL_QUESTIONS}
          </div>

          {/* Score */}
          <div className="flex items-center gap-1.5 shrink-0">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M7 1l1.5 3H12l-2.5 2 1 3L7 7.5 3.5 9l1-3L2 4h3.5L7 1z" fill="#D97706" />
            </svg>
            <span className="text-sm font-semibold font-mono-nums" style={{ color: '#D97706' }}>{score}</span>
            <span className="text-xs hidden sm:inline" style={{ color: '#94A3B8' }}>correct</span>
          </div>
        </div>

        {/* Progress bar with milestones */}
        <div className="relative pb-0">
          {/* Milestone dots */}
          <div className="absolute top-0 left-0 right-0 h-1.5 pointer-events-none" aria-hidden="true">
            {MILESTONES.map((m) => {
              const pct = (m / totalQuestions) * 100;
              const reached = currentQuestion > m;
              return (
                <div
                  key={`milestone-${m}`}
                  className="absolute top-0 w-1.5 h-1.5 rounded-full -translate-x-1/2 transition-all duration-500"
                  style={{
                    left: `${pct}%`,
                    background: reached ? '#1E40AF' : '#CBD5E1',
                    zIndex: 2,
                    boxShadow: reached ? '0 0 4px rgba(30,64,175,0.5)' : 'none',
                  }}
                />
              );
            })}
          </div>

          {/* Progress track */}
          <div
            className="h-1.5 mb-0 rounded-full overflow-hidden"
            style={{ background: '#F1F5F9' }}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Quiz progress: ${progressPercent}% complete`}
          >
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progressPercent}%`,
                background: 'linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%)',
                boxShadow: progressPercent > 0 ? '0 0 8px rgba(59,130,246,0.4)' : 'none',
              }}
            />
          </div>
        </div>
      </div>
    </header>
  );
}